import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  CircularProgress,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EditIcon from '@mui/icons-material/Edit'
import ClearIcon from '@mui/icons-material/Clear'
import InsightsIcon from '@mui/icons-material/Insights'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  MultiTableConfigTable,
  MultiTable,
} from '../../types/gotypes'
import MultiTableTabs from './MultiTableTabs'
import FilterEditorModal from '../spec-editor/FilterEditorModal'

interface MultiTableHeaderProps {
  data: MultiTable,
  tables: MultiTableConfigTable[],
  activeTable: string,
  allMultiTables: MultiTable[],
  onOpenTable: (tablePath: string) => void,
  onRemoveTable: (index: number) => void,
  onUpdateFilter: (filter: string) => void,
  onSave: (newData: MultiTable) => Promise<void>,
  onDelete: () => Promise<void>,
  onCancel: () => void,
  onOpenAnalysis?: () => void,
  onDownloadXLSX?: () => void,
  downloadingXLSX?: boolean,
}

const MultiTableHeader: React.FC<MultiTableHeaderProps> = ({
  data,
  tables,
  activeTable,
  allMultiTables,
  onOpenTable,
  onRemoveTable,
  onUpdateFilter,
  onSave,
  onDelete,
  onCancel,
  onOpenAnalysis,
  onDownloadXLSX,
  downloadingXLSX,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [filter, setFilter] = useState('')
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const handleSave = async () => {
    // Check for name conflicts with other multi-tables
    const nameConflict = allMultiTables.find(
      mt => mt.id !== data.id && mt.config?.name === name.trim()
    )

    if (nameConflict) {
      window.alert(`Warning: A multi-table with the name "${name.trim()}" already exists. Please choose a different name to avoid overwriting it.`)
      return
    }

    // Check if tables have changed and confirm with user
    const originalTables = data.config?.tables || []
    const tablesChanged = JSON.stringify(originalTables) !== JSON.stringify(tables)

    if (tablesChanged && data.id !== 'new') {
      const confirmed = window.confirm('Are you sure you want to save changes to this multi-table?')
      if (!confirmed) {
        return
      }
    }

    await onSave({
      ...data,
      config: {
        ...data.config,
        name,
        description,
        global_filter: filter,
        tables: tables,
      },
    })
  }

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?\n\nThis action cannot be undone.`)) {
      return
    }
    await onDelete()
  }

  useEffect(() => {
    setName(data.config?.name || '')
    setDescription(data.config?.description || '')
    setFilter(data.config?.global_filter || '')
    onUpdateFilter(data.config?.global_filter || '')
  }, [
    data.id,                      // Only reset when switching to a different multi-table
    data.config?.name,            // Only reset when name changes from external source
    data.config?.description,     // Only reset when description changes from external source
    data.config?.global_filter,   // Only reset when global_filter changes from external source
  ])

  useEffect(() => {
    if (data.id === 'new' && nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }, [data.id])

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        mb: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          spacing={2}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: 220,
            }}
          >
            <Tooltip title={showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setShowAdvanced(prev => !prev)}
                  sx={{
                    transition: 'transform 0.2s ease',
                    transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              value={name}
              onChange={(event) => setName(event.target.value)}
              label="Multi-Table List name"
              placeholder="New Multi-Table List (change this name)"
              variant="outlined"
              size="small"
              fullWidth
              inputRef={nameInputRef}
            />
          </Box>

          <TextField
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value)
            }}
            onBlur={() => onUpdateFilter(filter)}
            label="Global filter"
            placeholder="enter your filter here"
            variant="outlined"
            size="small"
            sx={{ minWidth: 220, flex: { xs: '0 0 auto', md: 1 } }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.5 }}>
                  {filter && (
                    <Tooltip title="Clear filter">
                      <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setFilter('')
                          onUpdateFilter('')
                        }}
                      >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={filter ? 'Edit filter' : 'Add filter'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setIsFilterModalOpen(true)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
            >
              Cancel
            </Button>
            {data.id !== 'new' && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
            {data.id !== 'new' && onOpenAnalysis && (
              <Button
                variant="outlined"
                size="small"
                color="info"
                startIcon={<InsightsIcon />}
                onClick={onOpenAnalysis}
              >
                Insights
              </Button>
            )}
            {data.id !== 'new' && onDownloadXLSX && (
              <Button
                variant="outlined"
                size="small"
                color="info"
                startIcon={downloadingXLSX ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                onClick={onDownloadXLSX}
                disabled={downloadingXLSX}
              >
                XLSX
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!name.trim()}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        <Collapse in={showAdvanced} unmountOnExit>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              value={description || ''}
              onChange={(event) => setDescription(event.target.value)}
              label="Description"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}

            />
          </Stack>
        </Collapse>
      </Box>

      <MultiTableTabs
        tables={tables}
        selectedTablePath={activeTable}
        onTableSelect={onOpenTable}
        onRemoveTable={onRemoveTable}
      />

      <FilterEditorModal
        open={isFilterModalOpen}
        currentFilter={filter}
        onClose={() => setIsFilterModalOpen(false)}
        onSave={(newFilter) => {
          setFilter(newFilter)
          onUpdateFilter(newFilter)
        }}
      />
    </Box>
  )
}

export default MultiTableHeader
